'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface MarketToolbarProps {
  searchOpen: boolean;
  onSearchToggle: (open: boolean) => void;
  selectedFilter: string;
  onFilterChange: (filter: string) => void;
  lastUpdated: string;
  onCreateAlert?: () => void;
  alertCount?: number;
}

const FILTERS = [
  { id: 'all', label: 'All Assets' },
  { id: 'gainers', label: 'Top Gainers' },
  { id: 'losers', label: 'Top Losers' },
  { id: 'volume', label: 'High Volume' },
  { id: 'ai_signal', label: 'AI Signals' },
];

export default function MarketToolbar({
  searchOpen,
  onSearchToggle,
  selectedFilter,
  onFilterChange,
  lastUpdated,
  onCreateAlert,
  alertCount = 0,
}: MarketToolbarProps) {
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  return (
    <div className="sticky top-0 z-10 bg-[#1E293B] border-b border-[#334155] px-4 py-2 flex flex-wrap items-center gap-2">
      {/* Filter Pills */}
      <div className="flex items-center gap-1 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => onFilterChange(f.id)}
            className={`px-3 py-1 rounded text-xs font-medium transition-all duration-150 min-h-[32px] ${
              selectedFilter === f.id
                ? 'bg-sky-500 text-white' :'bg-[#334155] text-slate-400 hover:text-slate-200 hover:bg-[#475569]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1" />

      {/* Right Controls */}
      <div className="flex items-center gap-2">
        {searchOpen ? (
          <div className="flex items-center gap-2 bg-[#0F172A] border border-sky-500 rounded px-3 py-1">
            <Icon name="MagnifyingGlassIcon" size={14} variant="outline" className="text-sky-500" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search symbol... (Ctrl+F)"
              className="bg-transparent text-sm text-slate-200 placeholder-slate-500 outline-none w-48"
              onKeyDown={e => e.key === 'Escape' && onSearchToggle(false)}
            />
            <button onClick={() => onSearchToggle(false)}>
              <Icon name="XMarkIcon" size={14} variant="outline" className="text-slate-400 hover:text-slate-200" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => onSearchToggle(true)}
            className="flex items-center gap-1.5 px-3 py-1 bg-[#334155] hover:bg-[#475569] rounded text-xs text-slate-400 hover:text-slate-200 transition-all min-h-[32px]"
            title="Search (Ctrl+F)"
          >
            <Icon name="MagnifyingGlassIcon" size={14} variant="outline" />
            <span className="hidden sm:inline">Search</span>
            <span className="hidden md:inline text-slate-600 font-mono ml-1">Ctrl+F</span>
          </button>
        )}

        {/* Create Alert Button */}
        {onCreateAlert && (
          <button
            onClick={onCreateAlert}
            className="relative flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded text-xs text-amber-400 hover:text-amber-300 transition-all min-h-[32px]"
            title="Create Alert"
          >
            <Icon name="BellIcon" size={14} variant="outline" />
            <span className="hidden sm:inline">Alert</span>
            {alertCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {alertCount > 9 ? '9+' : alertCount}
              </span>
            )}
          </button>
        )}

        <Link
          href="/asset-analysis-screen"
          className="flex items-center gap-1.5 px-3 py-1 bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/30 rounded text-xs text-sky-400 hover:text-sky-300 transition-all min-h-[32px]"
        >
          <Icon name="ChartBarIcon" size={14} variant="outline" />
          <span className="hidden sm:inline">Asset Analysis</span>
        </Link>
      </div>

      {/* Live indicator */}
      {lastUpdated && (
        <div className="w-full flex items-center gap-1.5 text-xs text-slate-500 font-mono pt-1 border-t border-[#334155]">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Live · Updated {lastUpdated}
        </div>
      )}
    </div>
  );
}